import React, { useState } from "react";
import { Target, FileText, Users, TrendingUp, ChevronDown } from "lucide-react";
import RevealOnScroll from "./RevealOnScroll";
import { cn } from "@/lib/utils";

const ServicesSection = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const services = [
    {
      icon: Target,
      title: "Social-Media-Strategie & Zielgruppendefinition",
      description: "Strukturierte Zieldefinition und strategische Ausrichtung.",
      details: "Entwicklung einer klaren Strategie basierend auf Ihren Unternehmenszielen und präziser Definition der relevanten Zielgruppen.",
      color: "from-[#2c4a52] to-[#3d5d66]"
    },
    {
      icon: FileText,
      title: "Content-Planung & Content-Erstellung",
      description: "Geplante, konsistente Inhalte mit klarem Fokus.",
      details: "Erstellung eines strukturierten Redaktionsplans und Produktion hochwertiger Inhalte, die Ihre Botschaft klar kommunizieren.",
      color: "from-[#3d5d66] to-[#4a9da8]"
    },
    {
      icon: Users,
      title: "Laufende Social-Media-Betreuung",
      description: "Regelmäßige Betreuung der Kanäle bei voller Transparenz.",
      details: "Kontinuierliche Pflege Ihrer Social-Media-Präsenz mit regelmäßigen Updates und vollständiger Nachvollziehbarkeit aller Aktivitäten.",
      color: "from-[#4a9da8] to-[#2c4a52]"
    },
    {
      icon: TrendingUp,
      title: "Werbekampagnen & Performance",
      description: "Datenbasierte Kampagnen mit laufender Auswertung.",
      details: "Planung und Umsetzung von Werbekampagnen mit kontinuierlicher Performance-Analyse und Optimierung basierend auf messbaren Ergebnissen.",
      color: "from-[#2c4a52] to-[#4a9da8]"
    }
  ];

  return (
    <section className="section-spacing bg-gradient-to-b from-[#0f1419] to-[#1a2332] relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#4a9da8]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#2c4a52]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="max-w-7xl mx-auto">
          <RevealOnScroll className="text-center mb-20">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 bg-[#4a9da8]/10 text-[#4a9da8] text-sm font-semibold rounded-full border border-[#4a9da8]/20">
                Unsere Leistungen
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 break-words">
              Bestandteile der
              <br />
              <span className="gradient-text">Rundumbetreuung</span>
            </h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              Alles aus einer Hand für Ihre erfolgreiche Social-Media-Präsenz
            </p>
          </RevealOnScroll>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              const isExpanded = expandedIndex === index;
              return (
                <RevealOnScroll
                  key={index}
                  variant="fade-up"
                  delay={index * 80}
                  as="div"
                >
                  <button
                    type="button"
                    onClick={() => setExpandedIndex(isExpanded ? null : index)}
                    className={cn(
                      "group card-hover interactive-card glass-dark p-8 rounded-2xl w-full text-left transition-all duration-300",
                      isExpanded && "ring-2 ring-[#4a9da8]/40"
                    )}
                  >
                    {/* Icon with gradient background */}
                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-[#4a9da8] transition-colors">
                      {service.title}
                    </h3>
                    
                    <p className="text-[#e8e4dc] leading-relaxed mb-4 font-medium">
                      {service.description}
                    </p>
                    
                    <div
                      className={cn(
                        "grid transition-all duration-300 ease-out",
                        isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                      )}
                    >
                      <div className="overflow-hidden">
                        <p className="text-white/60 text-sm leading-relaxed mb-6 pt-0">
                          {service.details}
                        </p>
                      </div>
                    </div>

                    {/* Expand/collapse indicator */}
                    <div className="flex items-center gap-2 text-[#4a9da8] transition-all duration-300">
                      <span className="text-sm font-semibold">
                        {isExpanded ? "Weniger anzeigen" : "Mehr erfahren"}
                      </span>
                      <ChevronDown className={cn("w-4 h-4 transition-transform duration-300", isExpanded && "rotate-180")} />
                    </div>
                  </button>
                </RevealOnScroll>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
